import { useRouteError, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Home, RefreshCcwIcon } from "lucide-react";
import { useEffect } from "react";
import posthog from "posthog-js";

export default function RouteErrorBoundary() {
  const error = useRouteError() as Error & { statusText?: string };
  const navigate = useNavigate();

  useEffect(() => {
    // Log to PostHog when error occurs
    const errorDetails = {
      message: error?.message || error?.statusText || "Unknown error",
      stack: error?.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };

    console.error("Route error details:", errorDetails);
    
    posthog.capture('route_error_boundary', errorDetails);
  }, [error]);

  const handleGoHome = () => {
    navigate("/dashboard");
  };

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-xl space-y-6 rounded-xl border border-destructive/50 border-dashed p-5">
        <div className="flex items-center gap-3">
          <div className="rounded-lg border border-dashed border-destructive/50 p-3">
            <AlertTriangle className="size-5 text-destructive" />
          </div>
          <div>
            <h1 className="text-xl font-medium text-foreground">
              Unexpected Application Error
            </h1>
            <p className="text-sm text-muted-foreground">
              Something went wrong
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">Error Details:</p>
          <div className="rounded-md bg-muted p-3">
            <p className="wrap-break-word text-xs font-mono text-muted-foreground">
              {error?.message || error?.statusText || "Unknown error occurred"}
            </p>
          </div>
        </div>

        {import.meta.env.DEV && error?.stack && (
          <details className="cursor-pointer rounded-md border border-border p-3">
            <summary className="text-sm font-medium text-foreground">
              Stack Trace (Dev Only)
            </summary>
            <pre className="mt-2 overflow-auto text-xs text-muted-foreground">
              {error.stack}
            </pre>
          </details>
        )}

        <div className="flex gap-2">
          <Button
            onClick={handleReload}
            className="flex-1"
            variant="neutral"
          >
            <RefreshCcwIcon />
            Reload Page
          </Button>
          <Button
            onClick={handleGoHome}
            className="flex-1"
            variant="secondary"
          >
            <Home/>
            Go Home
          </Button>
        </div>

        <p className="text-center text-xs text-muted-foreground">
          If this error persists, please contact support
        </p>
      </div>
    </div>
  );
}